/**
 * Sage Chat — Unified file processor
 *
 * Converts any supported file type into a format Sage can consume:
 * - Images → base64 (sent to Claude Vision)
 * - PDFs → page images + OCR text (existing pdfToContent)
 * - Word/Text/RTF → extracted plain text (injected into message)
 * - Spreadsheets → structured CSV text (injected into message)
 *
 * No server round-trip needed — all processing is client-side.
 */

import type { ImageAttachment, ImageMediaType } from '@/hooks/useSageChat';
import { pdfToContent, isPDFFile } from '@/lib/pdf/pdfToImages';
import {
  isImageFile,
  isWordFile,
  isSpreadsheetFile,
  isTextFile,
  SAGE_MAX_TEXT_CHARS,
} from './fileTypes';

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

export interface ProcessedImage {
  kind: 'image';
  attachment: ImageAttachment;
  previewUrl: string;
  file: File;
}

export interface ProcessedPdfPage {
  kind: 'pdf_page';
  attachment: ImageAttachment;
  previewUrl: string;
  pageNumber: number;
  pdfFilename: string;
}

export interface ProcessedPdfText {
  kind: 'pdf_text';
  pdfFilename: string;
  ocrText: string;
  totalPages: number;
  strategy: 'hybrid' | 'text_only';
}

export interface ProcessedDocumentText {
  kind: 'document_text';
  filename: string;
  text: string;
  format: string; // 'docx' | 'doc' | 'xlsx' | 'csv' | 'txt' | 'rtf'
  wordCount: number;
  truncated: boolean;
}

export interface ProcessedError {
  kind: 'error';
  filename: string;
  reason: string;
}

export type ProcessedFile =
  | ProcessedImage
  | ProcessedPdfPage
  | ProcessedPdfText
  | ProcessedDocumentText
  | ProcessedError;

// ---------------------------------------------------------------------------
// Main processor
// ---------------------------------------------------------------------------

export async function processFileForSage(file: File): Promise<ProcessedFile[]> {
  try {
    // --- Images ---
    if (isImageFile(file)) {
      const result = await fileToBase64Image(file);
      return result ? [result] : [{ kind: 'error', filename: file.name, reason: 'Failed to read image' }];
    }

    // --- PDF ---
    if (isPDFFile(file)) {
      return await processPdf(file);
    }

    // --- Word documents ---
    if (isWordFile(file)) {
      return [await processWord(file)];
    }

    // --- Spreadsheets ---
    if (isSpreadsheetFile(file)) {
      return [await processSpreadsheet(file)];
    }

    // --- Plain text / RTF ---
    if (isTextFile(file)) {
      return [await processText(file)];
    }

    return [{ kind: 'error', filename: file.name, reason: `Unsupported file type: ${file.type || 'unknown'}` }];
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown error';
    return [{ kind: 'error', filename: file.name, reason: msg }];
  }
}

// ---------------------------------------------------------------------------
// Image handler
// ---------------------------------------------------------------------------

async function fileToBase64Image(file: File): Promise<ProcessedImage | null> {
  const validTypes: ImageMediaType[] = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
  if (!validTypes.includes(file.type as ImageMediaType)) return null;

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      resolve({
        kind: 'image',
        attachment: {
          type: 'image',
          data: base64,
          mediaType: file.type as ImageMediaType,
          filename: file.name,
        },
        previewUrl: URL.createObjectURL(file),
        file,
      });
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

// ---------------------------------------------------------------------------
// PDF handler (delegates to existing pdfToContent)
// ---------------------------------------------------------------------------

async function processPdf(file: File): Promise<ProcessedFile[]> {
  // Cap text extraction so large text-native PDFs (e.g. a 576-page report) don't
  // hang/OOM walking every page — we only keep up to SAGE_MAX_TEXT_CHARS anyway.
  const pdfContent = await pdfToContent(file, { maxTextChars: SAGE_MAX_TEXT_CHARS });
  const results: ProcessedFile[] = [];

  for (const page of pdfContent.images) {
    results.push({
      kind: 'pdf_page',
      attachment: {
        type: 'image',
        data: page.base64,
        mediaType: 'image/png',
        filename: `${file.name} (page ${page.pageNumber})`,
      },
      previewUrl: page.dataUrl,
      pageNumber: page.pageNumber,
      pdfFilename: file.name,
    });
  }

  if (pdfContent.ocrText && (pdfContent.strategy === 'hybrid' || pdfContent.strategy === 'text_only')) {
    let ocrText = pdfContent.ocrText;
    if (ocrText.length > SAGE_MAX_TEXT_CHARS) {
      ocrText = ocrText.slice(0, SAGE_MAX_TEXT_CHARS) +
        `\n\n[... ${pdfContent.totalPages}-page document truncated at 100,000 characters]`;
    }
    results.push({
      kind: 'pdf_text',
      pdfFilename: file.name,
      ocrText,
      totalPages: pdfContent.totalPages,
      strategy: pdfContent.strategy,
    });
  }

  // A large PDF that yielded no page images AND no extractable text (e.g. a pure
  // scan) is the only case we can't handle here — surface it clearly instead of
  // silently producing an empty attachment.
  if (results.length === 0) {
    results.push({
      kind: 'error',
      filename: file.name,
      reason: 'No readable text or pages found in this PDF',
    });
  }

  return results;
}

// ---------------------------------------------------------------------------
// Word handler (mammoth — already installed)
// ---------------------------------------------------------------------------

async function processWord(file: File): Promise<ProcessedDocumentText | ProcessedError> {
  try {
    const mammoth = await import('mammoth');
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    let text = result.value.trim();

    if (!text) {
      return { kind: 'error', filename: file.name, reason: 'No text content found in document' };
    }

    const truncated = text.length > SAGE_MAX_TEXT_CHARS;
    if (truncated) {
      text = text.slice(0, SAGE_MAX_TEXT_CHARS) + '\n\n[... document truncated at 100,000 characters]';
    }

    const wordCount = text.split(/\s+/).filter(Boolean).length;
    const ext = file.name.toLowerCase().endsWith('.doc') ? 'doc' : 'docx';

    return { kind: 'document_text', filename: file.name, text, format: ext, wordCount, truncated };
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Failed to parse Word document';
    return { kind: 'error', filename: file.name, reason: msg };
  }
}

// ---------------------------------------------------------------------------
// Spreadsheet handler (SheetJS / xlsx)
// ---------------------------------------------------------------------------

async function processSpreadsheet(file: File): Promise<ProcessedDocumentText | ProcessedError> {
  try {
    const XLSX = await import('xlsx');
    const arrayBuffer = await file.arrayBuffer();

    // CSV: just read as text directly (faster, preserves formatting)
    if (file.name.toLowerCase().endsWith('.csv') || file.type === 'text/csv') {
      const decoder = new TextDecoder('utf-8');
      let text = decoder.decode(arrayBuffer).trim();
      const truncated = text.length > SAGE_MAX_TEXT_CHARS;
      if (truncated) {
        text = text.slice(0, SAGE_MAX_TEXT_CHARS) + '\n\n[... CSV truncated at 100,000 characters]';
      }
      const rowCount = text.split('\n').length;
      return {
        kind: 'document_text',
        filename: file.name,
        text,
        format: 'csv',
        wordCount: rowCount, // row count more useful than word count for spreadsheets
        truncated,
      };
    }

    // XLSX / XLS: parse with SheetJS
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const parts: string[] = [];
    let totalRows = 0;

    for (const sheetName of workbook.SheetNames) {
      const sheet = workbook.Sheets[sheetName];
      const csv = XLSX.utils.sheet_to_csv(sheet);
      const rows = csv.split('\n').filter((r: string) => r.trim()).length;
      totalRows += rows;

      if (workbook.SheetNames.length > 1) {
        parts.push(`--- Sheet: ${sheetName} (${rows} rows) ---\n${csv}`);
      } else {
        parts.push(csv);
      }
    }

    let text = parts.join('\n\n');
    const truncated = text.length > SAGE_MAX_TEXT_CHARS;
    if (truncated) {
      text = text.slice(0, SAGE_MAX_TEXT_CHARS) + '\n\n[... spreadsheet truncated at 100,000 characters]';
    }

    const ext = file.name.toLowerCase().endsWith('.xls') ? 'xls' : 'xlsx';

    return {
      kind: 'document_text',
      filename: file.name,
      text,
      format: ext,
      wordCount: totalRows,
      truncated,
    };
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Failed to parse spreadsheet';
    return { kind: 'error', filename: file.name, reason: msg };
  }
}

// ---------------------------------------------------------------------------
// Plain text / RTF handler
// ---------------------------------------------------------------------------

function stripRtf(rtf: string): string {
  // Remove RTF control words, braces, and hex escapes
  return rtf
    .replace(/\{\\[*]?[a-z]+[^}]*\}/g, '') // remove groups like {\fonttbl...}
    .replace(/\\[a-z]+[-]?\d*\s?/g, '')     // remove control words
    .replace(/[{}]/g, '')                     // remove remaining braces
    .replace(/\\'/g, '')                       // remove escaped quotes
    .replace(/\\[0-9a-f]{2}/gi, '')            // remove hex char escapes
    .replace(/\r\n/g, '\n')                  // normalize line endings
    .trim();
}

async function processText(file: File): Promise<ProcessedDocumentText | ProcessedError> {
  try {
    const rawText = await file.text();
    const isRtf = file.name.toLowerCase().endsWith('.rtf') || file.type.includes('rtf');
    let text = isRtf ? stripRtf(rawText) : rawText.trim();

    if (!text) {
      return { kind: 'error', filename: file.name, reason: 'File is empty' };
    }

    const truncated = text.length > SAGE_MAX_TEXT_CHARS;
    if (truncated) {
      text = text.slice(0, SAGE_MAX_TEXT_CHARS) + '\n\n[... file truncated at 100,000 characters]';
    }

    const wordCount = text.split(/\s+/).filter(Boolean).length;
    const format = isRtf ? 'rtf' : 'txt';

    return { kind: 'document_text', filename: file.name, text, format, wordCount, truncated };
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Failed to read text file';
    return { kind: 'error', filename: file.name, reason: msg };
  }
}
